import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import DashboardLayout from '../../components/DashboardLayout';
import { Skeleton } from '../../components/Skeleton';
import Icon from '../../components/Icon';
import { formatDateTime } from '../../lib/time';

export default function AdminCoupons() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ code: '', discountPercent: '', maxRedemptions: '', validUntil: '', note: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.adminCoupons().then((r) => setCoupons(r.coupons));

  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      await api.adminCreateCoupon({
        code: form.code,
        discountPercent: form.discountPercent,
        maxRedemptions: form.maxRedemptions || undefined,
        validUntil: form.validUntil || undefined,
        note: form.note || undefined,
      });
      setForm({ code: '', discountPercent: '', maxRedemptions: '', validUntil: '', note: '' });
      setAdding(false);
      await load();
    } catch (e2) {
      setErr(e2.message || t('admin.err_generic'));
    } finally {
      setBusy(false);
    }
  };

  const disable = async (id) => {
    await api.adminDeleteCoupon(id);
    load();
  };

  return (
    <DashboardLayout title={t('admin.coupons_title')} crumb={t('admin.title')} heading={false}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title">{t('admin.coupons_title')}</h1>
        {!adding && <button className="btn btn-sm" onClick={() => setAdding(true)}><Icon name="plus" size={14} /> {t('admin.new_coupon')}</button>}
      </div>

      {adding && (
        <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
          <form onSubmit={submit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <div className="field"><label>{t('admin.coupon_code')}</label>
                <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required placeholder="YAZ25" /></div>
              <div className="field"><label>{t('admin.coupon_pct')}</label>
                <input type="number" min="1" max="100" value={form.discountPercent} onChange={(e) => setForm({ ...form, discountPercent: e.target.value })} required /></div>
              <div className="field"><label>{t('admin.coupon_max')}</label>
                <input type="number" min="1" value={form.maxRedemptions} onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })} /></div>
              <div className="field"><label>{t('admin.coupon_until')}</label>
                <input type="date" value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} /></div>
            </div>
            <div className="field"><label>{t('admin.coupon_note')}</label>
              <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            {err && <div className="form-error"><Icon name="alert-triangle" size={14} /> {err}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-sm" disabled={busy}>{busy ? '…' : t('admin.coupon_create')}</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setAdding(false); setErr(''); }}>{t('common.cancel')}</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="card">{[0, 1, 2].map((i) => <Skeleton key={i} className="sk-text" style={{ margin: '14px 0' }} />)}</div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="list">
            {coupons.map((c) => (
              <div className="list-row" key={c.id} style={{ padding: 'var(--sp-4)', flexWrap: 'wrap', gap: 10 }}>
                <span className={`badge ${c.active ? 'ok' : ''}`}>{c.discountPercent}%</span>
                <div className="row-main">
                  <code style={{ fontSize: 13.5, color: 'var(--accent)' }}>{c.code}</code>
                  {c.assignedToUserId && <span className="badge" style={{ marginLeft: 8, fontSize: 10 }}>{t('admin.coupon_assigned')}</span>}
                  <div style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 2 }}>
                    {t('admin.coupon_used', { n: c.redeemedCount })} / {c.maxRedemptions ?? t('admin.coupon_unlimited')}
                    {c.validUntil && ` · ${formatDateTime(c.validUntil, lang).split(' ')[0]}`}
                    {c.note && ` · ${c.note}`}
                  </div>
                </div>
                {c.active ? (
                  <button className="btn btn-ghost btn-sm" onClick={() => disable(c.id)}>{t('admin.coupon_disable')}</button>
                ) : (
                  <span className="badge">{t('admin.coupon_inactive')}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
